import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import styles from './AdminOperations.module.css';

const CREATE_OPTIONS = [
  { id: 'shopify', label: 'Online order stock', source: 'shopify', purpose: '' },
  { id: 'sale', label: 'In-person sale', source: 'admin', purpose: 'sale' },
  { id: 'giveaway', label: 'Giveaway', source: 'admin', purpose: 'giveaway' },
  { id: 'promo', label: 'Promo', source: 'admin', purpose: 'promo' },
];

const PRESETS = {
  total: { status: '', source: '', programmed: '' },
  shopify: { status: 'available', source: 'shopify', programmed: '' },
  admin: { status: 'available', source: 'admin', programmed: '' },
  pending: { status: 'reserved', source: '', programmed: '' },
  active: { status: 'active', source: '', programmed: '' },
  unprogrammed: { status: '', source: '', programmed: 'false' },
  revoked: { status: 'revoked', source: '', programmed: '' },
};

function accountLabel(card) {
  const assigned = card?.assignedUserId;
  if (assigned && typeof assigned === 'object') return assigned.email || assigned.name || 'Linked';
  return '—';
}

function typeLabel(card) {
  if (card.source === 'replacement') return 'Replacement';
  if (card.source === 'shopify') return 'Online order stock';
  if (card.purpose === 'sale') return 'In-person sale';
  if (card.purpose === 'giveaway') return 'Giveaway';
  if (card.purpose === 'promo') return 'Promo';
  if (card.source === 'admin') return 'Admin';
  return card.source || '—';
}

function statusLabel(status) {
  if (status === 'available') return 'Available';
  if (status === 'reserved') return 'Activation pending';
  if (status === 'active') return 'Active';
  if (status === 'revoked') return 'Lost / revoked';
  return status || '—';
}

function statusTone(status) {
  if (status === 'active') return styles.good;
  if (status === 'reserved') return styles.warn;
  if (status === 'revoked') return styles.bad;
  return styles.neutral;
}

function shortId(value) {
  const text = String(value || '');
  if (text.length <= 14) return text || '—';
  return `${text.slice(0, 6)}…${text.slice(-4)}`;
}

function holderId(card) {
  const assigned = card?.assignedUserId;
  if (assigned && typeof assigned === 'object') return assigned._id || assigned.id || null;
  return assigned || card?.reservedUserId || null;
}

export default function AdminCardsPage() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const [cards, setCards] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [programmed, setProgrammed] = useState('');
  const [kind, setKind] = useState('shopify');
  const [quantity, setQuantity] = useState(10);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [reserveEmail, setReserveEmail] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.listLedgerCards({
        ...(status ? { status } : {}),
        ...(source ? { source } : {}),
        ...(programmed ? { programmed } : {}),
        ...(appliedQuery ? { q: appliedQuery } : {}),
        limit: 100,
      });
      const next = res.data?.cards || [];
      setCards(next);
      setTotal(res.data?.total ?? next.length);
      setInventory(res.data?.inventory || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, programmed, source, status]);

  useEffect(() => {
    const timer = setTimeout(() => setAppliedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    registerShellMeta?.({ title: 'Cards', subtitle: 'Physical inventory' });
    setRefreshHandler?.(load);
    load();
  }, [load, registerShellMeta, setRefreshHandler]);

  const applyPreset = (preset) => {
    const next = PRESETS[preset];
    const already = status === next.status && source === next.source && programmed === next.programmed;
    if (already && preset !== 'total') {
      setStatus('');
      setSource('');
      setProgrammed('');
      return;
    }
    setStatus(next.status);
    setSource(next.source);
    setProgrammed(next.programmed);
  };

  const pressed = (preset) => {
    const next = PRESETS[preset];
    return status === next.status && source === next.source && programmed === next.programmed;
  };

  const createCards = async (event) => {
    event.preventDefault();
    const option = CREATE_OPTIONS.find((item) => item.id === kind);
    setSaving(true);
    setError('');
    try {
      const res = await adminApi.generateCardBatch({
        quantity: Number(quantity),
        source: option.source,
        ...(option.purpose ? { purpose: option.purpose } : {}),
        label: option.label,
      });
      setCreated(res.data?.programming || []);
      setBatchId(res.data?.batch?._id || res.data?.batch?.id || '');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadBatch = async () => {
    if (!batchId) return;
    const csv = await adminApi.exportCardBatch(batchId);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'card-programming.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const runAction = async (work) => {
    setError('');
    try {
      await work();
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const selected = cards.find((card) => card.cardId === selectedId) || null;
  const count = (value) => (inventory ? value : '—');

  return (
    <div className={styles.page}>
      <section className={`${styles.kpis} ${styles.kpisCards}`} aria-label="Card inventory">
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('total')} onClick={() => applyPreset('total')}>
          <p className={styles.kpiLabel}>Total cards</p>
          <p className={styles.kpiValue}>{count(inventory?.total)}</p>
        </button>
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('shopify')} onClick={() => applyPreset('shopify')}>
          <p className={styles.kpiLabel}>Shopify stock</p>
          <p className={styles.kpiValue}>{count(inventory?.shopifyStock)}</p>
          <p className={styles.kpiHint}>Available</p>
        </button>
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('admin')} onClick={() => applyPreset('admin')}>
          <p className={styles.kpiLabel}>Admin stock</p>
          <p className={styles.kpiValue}>{count(inventory?.adminStock)}</p>
          <p className={styles.kpiHint}>Available</p>
        </button>
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('pending')} onClick={() => applyPreset('pending')}>
          <p className={styles.kpiLabel}>Activation pending</p>
          <p className={styles.kpiValue}>{count(inventory?.activationPending)}</p>
        </button>
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('active')} onClick={() => applyPreset('active')}>
          <p className={styles.kpiLabel}>Active</p>
          <p className={styles.kpiValue}>{count(inventory?.active)}</p>
        </button>
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('unprogrammed')} onClick={() => applyPreset('unprogrammed')}>
          <p className={styles.kpiLabel}>Not programmed</p>
          <p className={styles.kpiValue}>{count(inventory?.unprogrammed)}</p>
        </button>
        <button type="button" className={styles.kpiButton} aria-pressed={pressed('revoked')} onClick={() => applyPreset('revoked')}>
          <p className={styles.kpiLabel}>Lost / revoked</p>
          <p className={styles.kpiValue}>{count(inventory?.revoked)}</p>
        </button>
      </section>

      {error ? <p className={styles.error} role="alert">{error}</p> : null}

      <form className={styles.create} onSubmit={createCards}>
        <label className={styles.field}>
          Create
          <select value={kind} aria-label="Card purpose" onChange={(event) => setKind(event.target.value)}>
            {CREATE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          Quantity
          <input
            type="number"
            min="1"
            max="500"
            value={quantity}
            aria-label="Quantity"
            onChange={(event) => setQuantity(event.target.value)}
          />
        </label>
        <button type="submit" className="btn" disabled={saving}>{saving ? 'Creating…' : 'Create cards'}</button>
        {created.length > 0 ? (
          <div className={styles.programming}>
            {created.map((row) => (
              <a key={row.cardId} href={row.url}>{row.url}</a>
            ))}
            {batchId ? (
              <button type="button" className="btn" onClick={downloadBatch}>Download programming CSV</button>
            ) : null}
          </div>
        ) : null}
      </form>

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          value={query}
          placeholder="Search card or reservation"
          aria-label="Search cards"
          onChange={(event) => setQuery(event.target.value)}
        />
        <label className={styles.field}>
          Status
          <select aria-label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All statuses</option>
            <option value="available">Available</option>
            <option value="reserved">Activation pending</option>
            <option value="active">Active</option>
            <option value="revoked">Lost / revoked</option>
          </select>
        </label>
        <label className={styles.field}>
          Source
          <select aria-label="Source" value={source} onChange={(event) => setSource(event.target.value)}>
            <option value="">All sources</option>
            <option value="shopify">Shopify</option>
            <option value="admin">Admin</option>
            <option value="replacement">Replacement</option>
          </select>
        </label>
        <label className={styles.field}>
          Programmed
          <select aria-label="Programmed" value={programmed} onChange={(event) => setProgrammed(event.target.value)}>
            <option value="">Programmed or not</option>
            <option value="true">Programmed</option>
            <option value="false">Not programmed</option>
          </select>
        </label>
      </div>

      {loading ? <p className={styles.loading}>Loading cards…</p> : null}
      {!loading && cards.length === 0 ? <p className={styles.empty}>No cards in this view.</p> : null}
      {!loading && total > cards.length ? (
        <p className={styles.notice}>Showing {cards.length} of {total}.</p>
      ) : null}

      {cards.length > 0 ? (
        <div className={styles.list}>
          <div className={`${styles.head} ${styles.cards}`}>
            <span>Card</span>
            <span>Status</span>
            <span>Type</span>
            <span>Programmed</span>
            <span>Account</span>
            <span>Order</span>
            <span>Reserved</span>
          </div>
          {cards.map((card) => (
            <button
              key={card.cardId}
              type="button"
              className={`${styles.row} ${styles.cards}`}
              aria-current={selectedId === card.cardId ? 'true' : undefined}
              onClick={() => setSelectedId(card.cardId)}
            >
              <span className={`${styles.cell} ${styles.mono}`} data-label="Card" title={card.cardId}>{shortId(card.cardId)}</span>
              <span className={styles.cell} data-label="Status">
                <span className={`${styles.badge} ${statusTone(card.status)}`}>{statusLabel(card.status)}</span>
              </span>
              <span className={styles.cell} data-label="Type">{typeLabel(card)}</span>
              <span className={styles.cell} data-label="Programmed">
                <span className={`${styles.badge} ${card.programmed ? styles.good : styles.warn}`}>{card.programmed ? 'Yes' : 'No'}</span>
              </span>
              <span className={styles.cell} data-label="Account">{accountLabel(card)}</span>
              <span className={`${styles.cell} ${styles.mono}`} data-label="Order">{card.shopifyOrder?.orderId || '—'}</span>
              <span className={styles.cell} data-label="Reserved">{card.reservedFor || '—'}</span>
            </button>
          ))}
        </div>
      ) : null}

      {selected ? (
        <div className="admin-drawer-overlay" onClick={() => setSelectedId(null)} role="presentation">
          <div
            className="admin-drawer"
            role="dialog"
            aria-label={`Card ${selected.cardId}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-drawer-header">
              <h2>Card</h2>
              <button type="button" className="admin-drawer-close" onClick={() => setSelectedId(null)} aria-label="Close">×</button>
            </div>
            <div className="admin-drawer-body">
              <dl className={styles.facts}>
                <dt>Card ID</dt>
                <dd className={styles.mono}>{selected.cardId}</dd>
                <dt>Type</dt>
                <dd>{typeLabel(selected)}</dd>
                <dt>Status</dt>
                <dd>{statusLabel(selected.status)}</dd>
                <dt>Programmed</dt>
                <dd>{selected.programmed ? 'Yes' : 'No'}</dd>
                <dt>Account</dt>
                <dd>{accountLabel(selected)}</dd>
                <dt>Activated</dt>
                <dd>{selected.assignedAt ? new Date(selected.assignedAt).toLocaleString('en-GB') : '—'}</dd>
                <dt>Reserved for</dt>
                <dd>{selected.reservedFor || '—'}</dd>
                <dt>Shopify order</dt>
                <dd>{selected.shopifyOrder?.orderId || '—'}</dd>
                <dt>Replaces</dt>
                <dd className={styles.mono}>{selected.replacesCardId || '—'}</dd>
                <dt>NFC URL</dt>
                <dd><a href={`https://velox-network.com/nfc/${selected.cardId}`}>/nfc/{selected.cardId}</a></dd>
              </dl>
              <div className={styles.actions}>
                {!selected.programmed ? (
                  <button type="button" className="btn" onClick={() => runAction(() => adminApi.markCardProgrammed(selected.cardId))}>
                    Mark programmed
                  </button>
                ) : null}
                {selected.status === 'available' && selected.source === 'admin' ? (
                  <form
                    className={styles.actions}
                    onSubmit={(event) => {
                      event.preventDefault();
                      runAction(() => adminApi.reserveCard(selected.cardId, { email: reserveEmail }));
                    }}
                  >
                    <label className={styles.field}>
                      Reserve for
                      <input
                        type="email"
                        required
                        aria-label="Reserve for email"
                        value={reserveEmail}
                        onChange={(event) => setReserveEmail(event.target.value)}
                      />
                    </label>
                    <button type="submit" className="btn">Reserve</button>
                  </form>
                ) : null}
                {selected.status !== 'revoked' ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      if (window.confirm('Mark this card lost? It stays with the current account.')) {
                        runAction(() => adminApi.revokeCard(selected.cardId, { reason: 'lost', physicalDisposition: 'lost' }));
                      }
                    }}
                  >
                    Mark lost
                  </button>
                ) : null}
                {holderId(selected) ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      if (window.confirm('Create a replacement card for this account?')) {
                        runAction(() => adminApi.reissueCard(selected.cardId, { reason: 'lost' }));
                      }
                    }}
                  >
                    Create replacement
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
